import { Ops } from '@willie/schemas'
import type { CompatibilityStatus, InstallationProfile, MeterMaker } from './constants'
import type { Installation } from './exports'

// Profiles we now install in-house on Efento, so Shayp should no longer be ordered for them.
export const SHAYP_RETIRED_PROFILES: readonly InstallationProfile[] = [
  'cyble',
  'cyble-dock',
  'itron-aquadis',
  'itron-flodis',
  'itron-flostar',
  'itron-narval',
  'diehl-izar',
  'diehl-dock',
  'maddalena-reed-one',
  'maddalena-reed-sjplus',
  'maddalena-reed-wmap'
]

// The makers behind those profiles. Flagged on their own because the profile is still optional at
// order time — an Itron with no profile set would otherwise walk past the profile rule.
// `madalenna` is the catalog's spelling, inherited from the original meter_maker enum.
export const SHAYP_RETIRED_METER_MAKERS: readonly MeterMaker[] = ['itron', 'diehl', 'madalenna']

export type OrderIssueReason =
  | 'compatibility_unchecked'
  | 'not_compatible'
  | 'profile_not_approved'
  | 'shayp_retired_maker'
  | 'shayp_retired_profile'
  | 'efento_unmapped_profile'

export type OrderIssueSeverity = 'block' | 'warning'

export interface OrderIssue {
  reason: OrderIssueReason
  severity: OrderIssueSeverity
  label: string
  detail: string
}

const shaypRetiredMakerWarning = (maker: MeterMaker): OrderIssue => ({
  reason: 'shayp_retired_maker',
  severity: 'warning',
  label: 'Should be Efento',
  detail: `We install ${Ops.MeterMakers.humanizeMeterMakerSlug(maker)} meters ourselves — consider switching the logger to Efento.`
})

const shaypRetiredProfileWarning = (profile: InstallationProfile): OrderIssue => ({
  reason: 'shayp_retired_profile',
  severity: 'warning',
  label: 'Should be Efento',
  detail: `We install ${profile} ourselves — consider switching the logger to Efento.`
})

const efentoUnmappedProfileBlock = (profile: InstallationProfile): OrderIssue => ({
  reason: 'efento_unmapped_profile',
  severity: 'block',
  label: 'No parts list',
  detail: `${profile} has no parts list yet, so Efento would not know what to ship.`
})

const COMPATIBILITY_ORDER_ISSUE: Record<CompatibilityStatus, OrderIssue | null> = {
  pending: {
    reason: 'compatibility_unchecked',
    severity: 'block',
    label: 'Not checked',
    detail: "Check the meter's compatibility before ordering."
  },
  in_analysis: {
    reason: 'compatibility_unchecked',
    severity: 'block',
    label: 'In analysis',
    detail: 'The meter is still in analysis: settle its compatibility before ordering.'
  },
  not_compatible: {
    reason: 'not_compatible',
    severity: 'block',
    label: 'Not compatible',
    detail: 'No module fits this meter.'
  },
  compatible_not_supported: {
    reason: 'profile_not_approved',
    severity: 'block',
    label: 'Not approved',
    detail: "Profile not supported: needs the hardware team's approval."
  },
  compatible_supported: null,
  compatible_approved: null
}

// An orderable compatibility always carries a profile, so only an unmapped one is left to catch.
const findEfentoOrderIssue = (installation: Installation): OrderIssue | null => {
  const profile = installation.installation_profile
  if (!profile || Ops.InstallationProfileSkus.installationProfileSkus(profile)) return null
  return efentoUnmappedProfileBlock(profile)
}

const findShaypOrderIssue = (installation: Installation): OrderIssue | null => {
  const maker = installation.meter_maker
  if (maker && SHAYP_RETIRED_METER_MAKERS.includes(maker)) return shaypRetiredMakerWarning(maker)

  const profile = installation.installation_profile
  if (profile && SHAYP_RETIRED_PROFILES.includes(profile)) return shaypRetiredProfileWarning(profile)

  return null
}

export const findOrderIssue = (installation: Installation): OrderIssue | null => {
  const compatibilityIssue = COMPATIBILITY_ORDER_ISSUE[installation.compatibility_status]
  if (compatibilityIssue) return compatibilityIssue
  if (installation.logger_maker === 'efento') return findEfentoOrderIssue(installation)
  if (installation.logger_maker === 'shayp') return findShaypOrderIssue(installation)
  return null
}

export const isOrderBlocked = (installation: Installation): boolean =>
  findOrderIssue(installation)?.severity === 'block'
